import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type LocalShadowMetrics, type LocalShadowSite } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * The local model shadow (CAP-718): every AI call site also asks the local
 * model, nothing it says reaches anyone, and this compares the two answers.
 * For replies, reviews and thank-yous it also judges both against what was
 * actually sent. Numbers only; the API never returns an answer's text.
 */

const WINDOWS = [7, 28, 90] as const

const FAILURE_LABEL: Record<string, string> = {
  queued: 'waiting for the box',
  gave_up: 'gave up waiting',
  timeout: 'timed out',
  network: 'network error',
  bad_json: 'not valid JSON',
  truncated: 'cut off at its token limit',
  unconfigured: 'not configured',
  unusable: 'answer production would reject',
  other: 'other error',
}

function failureLabel(category: string) {
  return FAILURE_LABEL[category] ?? category.replace(/^http_/, 'HTTP ')
}

/**
 * One decimal, never rounded up to a whole percent: the cutover rules have
 * thresholds like 90%, and 0.895 shown as "90%" would read as a pass.
 */
function pct(value: number | null) {
  return value === null ? null : `${(Math.floor(value * 1000 + 1e-9) / 10).toFixed(1)}%`
}

function seconds(ms: number | null) {
  return ms === null ? null : `${(ms / 1000).toFixed(1)}s`
}

const Dash = () => <span className="text-textMuted">-</span>

/**
 * Local failures plus unreadable local answers, still-queued rows aside:
 * what the cutover rules bound.
 */
function failedRows(m: LocalShadowMetrics) {
  return (
    Object.entries(m.local_failures)
      .filter(([category]) => category !== 'queued')
      .reduce((sum, [, n]) => sum + n, 0) + m.local_unusable
  )
}

function Failures({ metrics }: { metrics: LocalShadowMetrics }) {
  // Unreadable local answers are failures too, as the cutover rules count them.
  const entries = [
    ...Object.entries(metrics.local_failures),
    ...(metrics.local_unusable > 0 ? [['unusable', metrics.local_unusable] as const] : []),
  ].sort((a, b) => b[1] - a[1])
  const total = entries.reduce((sum, [, n]) => sum + n, 0)
  if (total === 0) return <Dash />
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="tabular-nums underline decoration-dotted underline-offset-2"
          aria-label={`${total} local failures: ${entries.map(([c, n]) => `${n} ${failureLabel(c)}`).join(', ')}`}
        >
          {total}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {entries.map(([category, n]) => (
          <div key={category}>
            {n} {failureLabel(category)}
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

function Agreement({ site, metrics }: { site: LocalShadowSite; metrics: LocalShadowMetrics }) {
  const value = pct(metrics.agreement)
  if (value === null) return <Dash />
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="tabular-nums underline decoration-dotted underline-offset-2"
          aria-label={`Agreement ${value} over ${metrics.compared} answers: ${site.agreement_measure ?? ''}`}
        >
          {value}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="font-semibold">Over {metrics.compared} answered rows</div>
        <div>{site.agreement_measure}</div>
        {metrics.local_unusable > 0 ? (
          <div className="mt-1">
            {metrics.local_unusable} local {metrics.local_unusable === 1 ? 'answer' : 'answers'}{' '}
            could not be read and counted as disagreeing
          </div>
        ) : null}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * "local 0.214 · cloud 0.340", lower is closer to what was sent. Three
 * decimals, as the API rounds them, so a gap near the rules' 0.05 margin is
 * never hidden by display rounding.
 */
function Judge({ site, metrics }: { site: LocalShadowSite; metrics: LocalShadowMetrics }) {
  const judge = metrics.judge
  if (!judge) return <span className="text-textMuted">Not sent</span>
  if (judge.judged === 0) return <span className="text-textMuted">None sent yet</span>
  const local = judge.local_mean_distance ?? 0
  const cloud = judge.cloud_mean_distance ?? 0
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-left tabular-nums">
          <span className={local < cloud ? 'font-semibold text-mint' : 'text-text'}>
            local {local.toFixed(3)}
          </span>
          <span className="text-textMuted"> · </span>
          <span className={cloud < local ? 'font-semibold text-text' : 'text-text'}>
            cloud {cloud.toFixed(3)}
          </span>
          <span className="block text-[11px] text-textMuted">{judge.judged} judged</span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="font-semibold">Mean distance to what was sent (lower is closer)</div>
        <div>{site.judge_measure}</div>
        <div className="mt-1">
          Local closer {judge.local_closer}, cloud closer {judge.cloud_closer}, tied {judge.ties}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function SiteName({ site }: { site: LocalShadowSite }) {
  return (
    <>
      <span className="mr-1.5 font-mono text-[11px] text-textMuted">{site.code ?? '?'}</span>
      <span className="font-medium text-text">{site.label}</span>
    </>
  )
}

function OverviewTable({ sites }: { sites: LocalShadowSite[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-xs">
        <caption className="sr-only">Local model shadow per call site</caption>
        <thead>
          <tr className="text-[10px] uppercase tracking-wide whitespace-nowrap text-textMuted">
            <th className="px-2 py-1 text-left font-medium">Call site</th>
            <th className="px-2 py-1 text-right font-medium">Rows</th>
            <th className="px-2 py-1 text-right font-medium">Local failed</th>
            <th className="px-2 py-1 text-right font-medium">Median time</th>
            <th className="px-2 py-1 text-right font-medium">Agreement</th>
            <th className="px-2 py-1 text-left font-medium">Against what was sent</th>
          </tr>
        </thead>
        <tbody>
          {sites.map(site => (
            <tr key={site.call_site} className="border-t border-border align-top">
              <td className="px-2 py-1.5 whitespace-nowrap">
                <SiteName site={site} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-text">{site.rows}</td>
              <td className="px-2 py-1.5 text-right">
                <Failures metrics={site} />
              </td>
              <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                {site.rows === 0 ? (
                  <Dash />
                ) : (
                  <>
                    <span className="text-text">local {seconds(site.median_local_ms) ?? '-'}</span>
                    <span className="block text-[11px] text-textMuted">
                      cloud {seconds(site.median_cloud_ms) ?? '-'}
                    </span>
                  </>
                )}
              </td>
              <td className="px-2 py-1.5 text-right">
                <Agreement site={site} metrics={site} />
              </td>
              <td className="px-2 py-1.5">
                <Judge site={site} metrics={site} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Agreement per site and week; a week a site had no rows in is a dash. */
function WeeklyTable({ sites }: { sites: LocalShadowSite[] }) {
  const weeks = [...new Set(sites.flatMap(site => site.weeks.map(w => w.week_start)))].sort()
  if (weeks.length === 0) return null
  const label = (week: string) =>
    new Date(`${week}T00:00:00Z`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-text">Agreement by week</h3>
      <div className="overflow-x-auto">
        <table className="w-auto text-xs">
          <caption className="sr-only">Local versus cloud agreement per call site and week</caption>
          <thead>
            <tr className="text-[10px] uppercase tracking-wide whitespace-nowrap text-textMuted">
              <th className="px-2 py-1 text-left font-medium">Call site</th>
              {weeks.map(week => (
                <th key={week} className="px-2 py-1 pl-8 text-right font-medium whitespace-nowrap">
                  Week of {label(week)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sites
              .filter(site => site.weeks.length > 0)
              .map(site => (
                <tr key={site.call_site} className="border-t border-border">
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <SiteName site={site} />
                  </td>
                  {weeks.map(week => {
                    const m = site.weeks.find(w => w.week_start === week)
                    return (
                      <td
                        key={week}
                        className="px-2 py-1.5 pl-8 text-right whitespace-nowrap tabular-nums"
                      >
                        {m ? (
                          <>
                            <span className="text-text">{pct(m.agreement) ?? '-'}</span>
                            <span className="block text-[11px] text-textMuted">
                              {m.rows} rows
                              {m.judge && m.judge.judged > 0 ? `, ${m.judge.judged} judged` : ''}
                              {failedRows(m) > 0 ? `, ${failedRows(m)} failed` : ''}
                            </span>
                          </>
                        ) : (
                          <Dash />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CutoverRules({ sites }: { sites: LocalShadowSite[] }) {
  const ruled = sites.filter(site => site.cutover_rule)
  if (ruled.length === 0) return null
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-text">Cutover rules</h3>
      <p className="text-xs text-textMuted">
        When a call site may switch to the local model. Every rule is a proposal until Andrei
        decides it on these numbers.
      </p>
      <ul className="space-y-1.5 text-xs">
        {ruled.map(site => (
          <li key={site.call_site} className="grid gap-1 sm:grid-cols-[190px_1fr] sm:gap-3">
            <span>
              <SiteName site={site} />
            </span>
            <span className="text-text">{site.cutover_rule}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function LocalShadowCard() {
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(28)
  const query = useQuery({
    queryKey: ['local-shadow-summary', days],
    queryFn: () => api.getLocalShadowSummary(days),
  })
  const data = query.data
  const totalRows = data?.sites.reduce((sum, site) => sum + site.rows, 0) ?? 0

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3 lg:flex-nowrap">
          <div className="min-w-0 flex-1 basis-80 space-y-1.5">
            <CardTitle>Local model shadow</CardTitle>
            <CardDescription>
              Every AI call also asks the local model, and nothing it answers reaches a guest or an
              operator. This compares the two answers, and for replies, reviews and thank-yous
              judges both against what was actually sent.
            </CardDescription>
          </div>
          <div role="group" aria-label="Window" className="flex shrink-0 gap-1">
            {WINDOWS.map(window => (
              <Button
                key={window}
                type="button"
                size="sm"
                variant={days === window ? 'default' : 'outline'}
                aria-pressed={days === window}
                onClick={() => setDays(window)}
              >
                {window} days
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {query.isLoading ? (
          <LoadingSkeleton className="h-48 w-full" />
        ) : query.error && !data ? (
          <ErrorState
            compact
            title="Could not load the shadow numbers"
            error={query.error}
            onRetry={() => query.refetch()}
          />
        ) : data ? (
          <>
            {query.error ? (
              <div
                role="alert"
                className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
              >
                <span>Could not refresh the shadow numbers; showing the last ones loaded.</span>
                <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()}>
                  Retry
                </Button>
              </div>
            ) : null}
            <p className="text-xs text-textMuted">
              {data.enabled.length === 0
                ? 'Shadow is off: no new rows are being recorded.'
                : data.enabled.includes('all')
                  ? 'Shadow is on for every call site.'
                  : `Shadow is on for ${data.enabled.join(', ')}.`}{' '}
              {totalRows === 0 ? `No rows in the last ${data.days} days.` : null}
            </p>
            <OverviewTable sites={data.sites} />
            <WeeklyTable sites={data.sites} />
            <CutoverRules sites={data.sites} />
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
